import { Flex, Stack as ChakraStack, usePrevious } from '@chakra-ui/react';
import {
  KeyIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Bleed, Card, Header, Heading, Stack, Text } from '@main/complykit';
import { useImpersonateUserMutation } from '@main/graphql/mutations/ImpersonateUser.admin.generated';
import {
  GetPlatformUsersQuery,
  GetPlatformUsersQueryVariables,
  useGetPlatformUsersQuery,
} from '@main/graphql/queries/GetPlatformUsers.admin.generated';
import { AuthPrincipalTypes_Enum, Users_Bool_Exp } from '@main/graphql/types.admin.generated';
import { Order_By } from '@main/graphql/types.generated';
import { useStableCallback } from '@main/shared/browser';
import {
  actionHelper,
  BulkSelectionToolbarPortal,
  createColumnHelper,
  errorToast,
  filterModeILikeComparison,
  filterModeIsEmpty,
  filterModeNegateCondition,
  Table,
  useAlertDialog,
  useDrawer,
  useServerPagination,
  useTableFiltersQuery,
  useTableSearchQuery,
} from '@main/ui';
import { Row } from '@tanstack/react-table';
import useDebounce from 'ahooks/es/useDebounce';
import equal from 'fast-deep-equal';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod/v4';

import { STATUSES } from '../organization/constants';
import { useUpdateUserPasswordAccessAction } from './disable-password-action';
import { useUpdateUserStateAction } from './disable-user-action';

const tableId = 'users';

export const ManageUsers = () => {
  const bulkSelectionToolbarPortalRef = useRef<HTMLElement>(null);
  const { refetchQuery, ...tableProps } = useUsersServerPaginatedQuery();
  const tableItemName = useMemo(() => {
    return {
      singular: 'user',
      plural: 'users',
    };
  }, []);

  return (
    <ChakraStack spacing={6}>
      <Heading size="xl">Users</Heading>
      <Card>
        <Header isDivided>
          <Flex align="center" justify="space-between" gap={4} w="full">
            <Stack gap={0}>
              <Heading size="md">Users</Heading>
              <Text size="sm" color="muted">
                All users currently registered to Complyance
              </Text>
            </Stack>
          </Flex>
        </Header>

        <Bleed>
          <Table
            id={tableId}
            entity="user"
            itemName={tableItemName}
            {...tableProps}
            bulkSelectionToolbarPortalRef={bulkSelectionToolbarPortalRef}
          />
        </Bleed>
      </Card>
      <BulkSelectionToolbarPortal ref={bulkSelectionToolbarPortalRef} />
    </ChakraStack>
  );
};

const queryConfig = {
  pageSize: 15,
  filterDebounceMs: 500,
  queryOptions: {
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true,
  },
};

export function useUsersServerPaginatedQuery() {
  const { columns, onRowClick } = useUsersTableColumns(
    useStableCallback(() => {
      query.refetch();
    }),
  );
  const [columnFilters, onColumnFiltersChange] = useTableFiltersQuery({
    columns,
    searchParam: 'usersFilter',
  });
  const [globalFilter, onGlobalFilterChange] = useTableSearchQuery({ searchParam: 'search' });
  const [pagination, tablePaginationProps] = useServerPagination({
    tableName: 'platform-users-table',
    columns,
    globalFilter: useDebounce(globalFilter, { wait: queryConfig.filterDebounceMs }),
    columnFilters: useDebounce(columnFilters, {
      wait: queryConfig.filterDebounceMs,
      leading: true,
    }),
    defaultPageSize: queryConfig.pageSize,
  });

  const variables: GetPlatformUsersQueryVariables = {
    where: {
      principalType: { _eq: AuthPrincipalTypes_Enum.Human },
      organization_users: {
        organization_id: { _is_null: false },
      },
      ...pagination.where,
    },
    orderBy: pagination.orderBy ?? {
      displayName: Order_By.Asc,
    },
    offset: pagination.offset,
    limit: pagination.limit,
  };

  const prevVariables = usePrevious(variables);

  const areVariablesEqual = equal(prevVariables, variables);

  const query = useGetPlatformUsersQuery(variables, {
    ...queryConfig.queryOptions,
  });

  const [isFilterLoading, setIsFilterLoading] = useState(false);

  useLayoutEffect(() => {
    if (!areVariablesEqual) {
      setIsFilterLoading(true);
    }
  }, [areVariablesEqual]);

  useLayoutEffect(() => {
    if (!query.isFetching) {
      setIsFilterLoading(false);
    }
  }, [query.isFetching]);

  return {
    ...tablePaginationProps,
    isLoading: query.isLoading || isFilterLoading,
    data: query.data?.users ?? [],
    rowCount: query.data?.usersAggregate.aggregate?.count,
    refetchQuery: query.refetch,
    columns,
    onRowClick,
    columnFilters,
    onColumnFiltersChange,
    globalFilter,
    onGlobalFilterChange,
  };
}

type User = GetPlatformUsersQuery['users'][number];
const uuidSchema = z.uuid();

function useUsersTableColumns(refetchQuery: () => void) {
  const { openDialog } = useAlertDialog();
  const drawer = useDrawer();
  const [impersonateUser] = useImpersonateUserMutation();
  const disablePasswordAccessAction = useUpdateUserPasswordAccessAction(refetchQuery);
  const disableUserAction = useDisableUserOnAllOrgsMutation(refetchQuery);

  return useMemo(() => {
    const columnHelper = createColumnHelper<User, Users_Bool_Exp>();
    const openUserDrawer = (userId: string) => drawer.open({ entity: 'user', entityId: userId });

    return {
      onRowClick: (row: Row<User>) => openUserDrawer(row.original.id),
      columns: [
        columnHelper.columns.status({
          id: 'status',
          header: 'Status',
          accessorFn: ({ organization_users }) => {
            const isActive = organization_users.some((user) => !user.disabled);
            const statusMeta = isActive ? STATUSES.active : STATUSES.disabled;

            return {
              value: statusMeta.value,
              tone: statusMeta.tone,
            };
          },
          size: 110,
          enableColumnFilter: true,
          getColumnFilterCondition(filterMode, filterValue) {
            return (
              filterModeIsEmpty(filterMode, (condition) => ({
                organization_users: { disabled: condition },
              })) ??
              filterModeNegateCondition(filterMode, {
                organization_users: {
                  disabled: {
                    _in: filterValue.map((value) => (value === 'Active' ? false : true)),
                  },
                  organization_id: { _is_null: false },
                },
              })
            );
          },
        }),
        columnHelper.columns.text({
          id: 'name',
          header: 'Name',
          accessorFn: ({ displayName }) => displayName,
          enableHiding: false,
          enableGlobalFilter: true,
          enableColumnFilter: true,
          getGlobalFilterCondition(filterValue) {
            return { displayName: { _ilike: `%${filterValue}%` } };
          },
          getColumnFilterCondition(filterMode, filterValue) {
            return {
              displayName: filterModeILikeComparison(filterMode, filterValue),
            };
          },
        }),
        columnHelper.columns.text({
          id: 'email',
          header: 'Email',
          accessorFn: ({ email }) => email,
          enableGlobalFilter: true,
          enableColumnFilter: true,
          getGlobalFilterCondition(filterValue) {
            return { email: { _ilike: `%${filterValue}%` } };
          },
          getColumnFilterCondition(filterMode, filterValue) {
            return {
              email: filterModeILikeComparison(filterMode, filterValue),
            };
          },
        }),
        columnHelper.columns.text({
          id: 'id',
          header: 'ID',
          accessorFn: ({ id }) => id,
          enableGlobalFilter: true,
          getGlobalFilterCondition(filterValue) {
            const { success } = uuidSchema.safeParse(filterValue);
            if (success) {
              return { id: { _eq: filterValue } };
            }
            return;
          },
        }),
        columnHelper.columns.text({
          id: 'organizations',
          header: 'Organizations #',
          accessorFn: ({ organization_users_aggregate }) =>
            organization_users_aggregate.aggregate?.count.toString(),
          size: 150,
        }),

        columnHelper.columns.tag({
          id: 'passwordAccess',
          header: 'Password access',
          accessorFn: ({ password_disabled }) => {
            return {
              value: password_disabled ? 'Disabled' : 'Enabled',
              colorScheme: password_disabled ? 'gray' : 'green',
            };
          },
          size: 150,
          enableColumnFilter: true,
          getColumnFilterCondition(filterMode, filterValue) {
            return (
              filterModeIsEmpty(filterMode, (condition) => ({
                password_disabled: condition,
              })) ??
              filterModeNegateCondition(filterMode, {
                password_disabled: {
                  _in: filterValue.map((value) => (value === 'Enabled' ? false : true)),
                },
              })
            );
          },
        }),
        columnHelper.columns.actions({
          menuActions: ({ row }) => [
            disableUserAction(row.original),
            actionHelper.menuActionItem({
              icon: UserIcon,
              label: 'Impersonate',
              onClick: async () => {
                try {
                  const result = await impersonateUser({ userId: row.original.id }).unwrap();
                  window.open(
                    `${import.meta.env.VITE_FRONTEND_URL}/impersonate?token=${
                      result.impersonate_user?.refreshToken
                    }`,
                  );
                } catch {
                  errorToast('unable to impersonate user');
                }
              },
            }),
            actionHelper.menuActionItem({
              icon: KeyIcon,
              label: row.original.password_disabled
                ? 'Enable password access'
                : 'Disable password access',
              onClick: () => {
                openDialog({
                  dialogHeader: row.original.password_disabled
                    ? 'Enable password access'
                    : 'Disable password access',
                  dialogContent: `Are you sure you want to ${row.original.password_disabled ? 'enabled' : 'disable'} password access for this user?`,
                  confirmAction: {
                    children: row.original.password_disabled ? 'Enable' : 'Disable',
                    onClick: () =>
                      disablePasswordAccessAction({
                        userIds: row.original.id,
                        isPasswordDisabled: !row.original.password_disabled,
                      }),
                  },
                });
              },
            }),
          ],
          meta: {
            bulkActions: [
              (rows) => {
                const userIds = rows.map((row) => row.original.id);
                const isPasswordDisabled = rows.some((row) => row.original.password_disabled);

                if (!isPasswordDisabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: KeyIcon,
                  label: 'Enable password access',
                  onClick: () =>
                    openDialog({
                      dialogHeader: 'Enable password access',
                      dialogContent:
                        'Are you sure you want to enable password access for these users?',
                      confirmAction: {
                        colorScheme: 'green',
                        children: 'Enable',
                        onClick: () =>
                          disablePasswordAccessAction({
                            userIds,
                            isPasswordDisabled: false,
                          }),
                      },
                    }),
                });
              },
              (rows) => {
                const userIds = rows.map((row) => row.original.id);
                const isPasswordEnabled = rows.some((row) => !row.original.password_disabled);

                if (!isPasswordEnabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: XMarkIcon,
                  label: 'Disable password access',
                  onClick: () =>
                    openDialog({
                      dialogHeader: 'Disable password access',
                      dialogContent:
                        'Are you sure you want to disable password access for these users?',
                      confirmAction: {
                        children: 'Disable',
                        onClick: () =>
                          disablePasswordAccessAction({
                            userIds,
                            isPasswordDisabled: true,
                          }),
                      },
                    }),
                });
              },
            ],
          },
        }),
      ],
    };
  }, [disablePasswordAccessAction, disableUserAction, drawer, impersonateUser, openDialog]);
}

const useDisableUserOnAllOrgsMutation = (refetchQuery: () => void) => {
  const { openDialog } = useAlertDialog();
  const bulkDisableUser = useUpdateUserStateAction(refetchQuery);

  return useStableCallback((user: User) => {
    const isActive = user.organization_users.some((user) => !user.disabled);

    return actionHelper.menuActionItem({
      icon: isActive ? UserMinusIcon : UserPlusIcon,
      label: isActive ? 'Disable' : 'Enable',
      onClick: () =>
        isActive
          ? openDialog({
              dialogHeader: 'Disable user',
              dialogContent:
                'Are you sure you want to disable this user for all his/her organizations?',
              confirmAction: {
                children: 'Disable',
                onClick: () =>
                  bulkDisableUser({
                    userIds: user.id,
                    organizationIds: user.organization_users.map((user) => user.organization_id),
                    disabled: true,
                  }),
              },
            })
          : bulkDisableUser({
              userIds: user.id,
              organizationIds: user.organization_users.map((user) => user.organization_id),
              disabled: false,
            }),
    });
  });
};
